import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  PhoneOff,
  Share2,
  Copy,
  Check,
  QrCode,
  Users,
  MessageSquare,
  Send,
  Pin,
  PinOff,
  Maximize2,
  Minimize2,
  Hand,
  Radio,
  Disc,
  Info,
  ChevronRight,
  ShieldCheck,
  Volume2,
  VolumeX,
  Sparkles,
  ArrowLeft,
  Settings,
  Smile,
  AlertCircle,
  SwitchCamera,
  Sliders,
  Camera
} from 'lucide-react';
import QRCode from 'qrcode';
import confetti from 'canvas-confetti';
import { GroupParticipant, GroupChatMessage, WebRTCConnectionState, NavigationScreen, AppSettings } from '@/types';
import { webrtcGroupMeetingService, WebRTCGroupMeetingService } from '@/services/webrtcGroupMeetingService';
import { formatDuration, downloadBlob, generateThumbnailFromBlob } from '@/utils/helpers';
import { PwaInstallButton } from '@/components/PwaInstallButton';

interface GroupMeetingScreenProps {
  settings: AppSettings;
  onNavigate: (screen: NavigationScreen) => void;
  initialRoomCode?: string;
  onMeetingRecorded?: (blob: Blob, durationSec: number, title: string, thumbnailUrl?: string) => void;
  isMobileGuest?: boolean;
  activeGuestTab?: 'group' | 'podcast';
  onSwitchGuestTab?: (tab: 'group' | 'podcast') => void;
}

// Sound Synthesis helper using Web Audio API
const playTone = (type: 'join' | 'leave' | 'hand' | 'message') => {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);

    const now = ctx.currentTime;
    if (type === 'join') {
      osc.frequency.setValueAtTime(440, now);
      osc.frequency.exponentialRampToValueAtTime(880, now + 0.18);
      gain.gain.setValueAtTime(0.12, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
      osc.start(now);
      osc.stop(now + 0.25);
    } else if (type === 'leave') {
      osc.frequency.setValueAtTime(660, now);
      osc.frequency.exponentialRampToValueAtTime(330, now + 0.18);
      gain.gain.setValueAtTime(0.1, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
      osc.start(now);
      osc.stop(now + 0.25);
    } else if (type === 'hand') {
      osc.frequency.setValueAtTime(523.25, now);
      osc.frequency.setValueAtTime(659.25, now + 0.1);
      gain.gain.setValueAtTime(0.12, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
      osc.start(now);
      osc.stop(now + 0.25);
    } else if (type === 'message') {
      osc.frequency.setValueAtTime(800, now);
      gain.gain.setValueAtTime(0.06, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
      osc.start(now);
      osc.stop(now + 0.12);
    }
  } catch {}
};

export const GroupMeetingScreen: React.FC<GroupMeetingScreenProps> = ({
  settings,
  onNavigate,
  initialRoomCode,
  onMeetingRecorded,
  isMobileGuest,
  activeGuestTab,
  onSwitchGuestTab
}) => {
  const isMobileDevice = typeof navigator !== 'undefined' && (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (typeof window !== 'undefined' && 'ontouchstart' in window && navigator.maxTouchPoints > 1 && window.innerWidth < 768)
  );

  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
  const [isFlippingCamera, setIsFlippingCamera] = useState(false);
  // Mode: 'lobby' (Pre-Join Green Room) or 'in-meeting' (Active Call)
  const [stage, setStage] = useState<'lobby' | 'in-meeting'>('lobby');

  // Lobby Inputs
  const [userName, setUserName] = useState<string>(() => {
    return localStorage.getItem('meetsplit_user_name') || 'Guest User';
  });
  const [roomCodeInput, setRoomCodeInput] = useState<string>(() => {
    if (initialRoomCode) return initialRoomCode;
    const urlMeet = new URLSearchParams(window.location.search).get('meet') || new URLSearchParams(window.location.search).get('group');
    return urlMeet || '';
  });
  const [lobbyAction, setLobbyAction] = useState<'create' | 'join'>('create');

  // Local Media Stream
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  useEffect(() => {
    localStreamRef.current = localStream;
  }, [localStream]);
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isHandRaised, setIsHandRaised] = useState(false);

  // In-Meeting State
  const [activeRoomCode, setActiveRoomCode] = useState<string>('');
  const [connectionStatus, setConnectionStatus] = useState<WebRTCConnectionState>('idle');
  const [participants, setParticipants] = useState<GroupParticipant[]>([]);
  const [chatMessages, setChatMessages] = useState<GroupChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [pinnedParticipantId, setPinnedParticipantId] = useState<string | null>(null);
  const [activeSpeakerId, setActiveSpeakerId] = useState<string | null>(null);

  // Active Call Timer
  const [callDurationSec, setCallDurationSec] = useState<number>(0);
  const callDurationRef = useRef<any>(null);

  // Drawers: 'none' | 'chat' | 'people' | 'info'
  const [activeDrawer, setActiveDrawer] = useState<'none' | 'chat' | 'people' | 'info'>('none');

  // Recording State
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDurationSec, setRecordingDurationSec] = useState(0);
  const recordingTimerRef = useRef<any>(null);

  // Notification banners
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Modals / QR
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const qrCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // Media Devices & Auto-Detection State
  const [videoDevices, setVideoDevices] = useState<{ deviceId: string; label: string }[]>([]);
  const [audioDevices, setAudioDevices] = useState<{ deviceId: string; label: string }[]>([]);
  const [selectedCameraId, setSelectedCameraId] = useState<string>('');
  const [selectedMicId, setSelectedMicId] = useState<string>('');
  const [micVolumeLevel, setMicVolumeLevel] = useState<number>(0);

  // Full Screen State & Ref ("sirf room wal scrren full screen hoga")
  const meetingRoomContainerRef = useRef<HTMLDivElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showDeviceSettingsModal, setShowDeviceSettingsModal] = useState(false);

  // References
  const lobbyVideoRef = useRef<HTMLVideoElement | null>(null);
  const chatBottomRef = useRef<HTMLDivElement | null>(null);

  const [mediaPermissionDenied, setMediaPermissionDenied] = useState(false);
  const [isRequestingMedia, setIsRequestingMedia] = useState(false);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(curr => (curr === msg ? null : curr));
    }, 4000);
  };

  // Enumerate & Auto-Detect Media Devices
  const enumerateMediaDevices = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) return { vDevs: [], aDevs: [] };
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const vDevs = devices
        .filter((d) => d.kind === 'videoinput')
        .map((d, i) => ({
          deviceId: d.deviceId,
          label: d.label || `Camera ${i + 1}`
        }));
      const aDevs = devices
        .filter((d) => d.kind === 'audioinput')
        .map((d, i) => ({
          deviceId: d.deviceId,
          label: d.label || `Microphone ${i + 1}`
        }));

      setVideoDevices(vDevs);
      setAudioDevices(aDevs);

      // Auto-detect and set defaults if current selection is invalid or empty
      setSelectedCameraId((prev) => {
        if (prev && vDevs.some(d => d.deviceId === prev)) return prev;
        return vDevs[0]?.deviceId ?? '';
      });

      setSelectedMicId((prev) => {
        if (prev && aDevs.some(d => d.deviceId === prev)) return prev;
        return aDevs[0]?.deviceId ?? '';
      });

      return { vDevs, aDevs };
    } catch (err) {
      console.warn('[Group Meeting] Enumerate devices error:', err);
      return { vDevs: [], aDevs: [] };
    }
  }, []);

  // Auto-detect newly plugged in or disconnected devices (Camera & Mic hot-plugging)
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.addEventListener) return;
    enumerateMediaDevices();

    const handleDeviceChange = async () => {
      const { vDevs } = await enumerateMediaDevices();
      showToast('Media hardware updated (Auto-detected hardware change)');

      const currentVideoTrack = localStreamRef.current?.getVideoTracks()[0];
      const hadLiveVideo = Boolean(currentVideoTrack && currentVideoTrack.readyState === 'live');

      if (!hadLiveVideo && vDevs.length > 0) {
        // Camera newly connected! Auto-activate video stream
        try {
          const stream = await acquireLobbyMedia(facingMode, vDevs[0].deviceId, selectedMicId);
          if (stream) {
            setIsVideoMuted(false);
            webrtcGroupMeetingService.updateLocalStream(stream);
            showToast(`Camera Auto-Detected: ${vDevs[0].label}`);
          }
        } catch {}
      } else if (hadLiveVideo && vDevs.length === 0) {
        // Camera unplugged! Fallback to audio-only cleanly
        try {
          const stream = await acquireLobbyMedia(facingMode, '', selectedMicId);
          if (stream) {
            setIsVideoMuted(true);
            webrtcGroupMeetingService.updateLocalStream(stream);
            showToast('Camera Disconnected • Switched to Audio-Only');
          }
        } catch {}
      }
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [enumerateMediaDevices, facingMode, selectedMicId]);

  // Full Screen Change Listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Room-only Fullscreen Toggle
  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        if (meetingRoomContainerRef.current) {
          if (meetingRoomContainerRef.current.requestFullscreen) {
            await meetingRoomContainerRef.current.requestFullscreen();
          } else if ((meetingRoomContainerRef.current as any).webkitRequestFullscreen) {
            await (meetingRoomContainerRef.current as any).webkitRequestFullscreen();
          }
        }
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          await (document as any).webkitExitFullscreen();
        }
      }
    } catch (err) {
      console.warn('[Fullscreen toggle error]', err);
    }
  };

  // Real-time Mic Activity Meter
  useEffect(() => {
    if (!localStream || isMicMuted) {
      setMicVolumeLevel(0);
      return;
    }
    const audioTrack = localStream.getAudioTracks()[0];
    if (!audioTrack || !audioTrack.enabled) {
      setMicVolumeLevel(0);
      return;
    }

    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 64;
      const source = ctx.createMediaStreamSource(localStream);
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      let animId: number;

      const checkVolume = () => {
        analyser.getByteFrequencyData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i];
        }
        const avg = sum / dataArray.length;
        setMicVolumeLevel(Math.min(100, Math.round((avg / 128) * 100)));
        animId = requestAnimationFrame(checkVolume);
      };

      animId = requestAnimationFrame(checkVolume);

      return () => {
        cancelAnimationFrame(animId);
        try { ctx.close(); } catch {}
      };
    } catch {}
  }, [localStream, isMicMuted]);

  // Progressive Stream Acquisition with auto-detection of available camera
  const acquireLobbyMedia = useCallback(async (
    targetFacing: 'user' | 'environment' = facingMode,
    targetCamId?: string,
    targetMicId?: string
  ): Promise<MediaStream | null> => {
    setIsRequestingMedia(true);
    setMediaPermissionDenied(false);

    const camId = targetCamId !== undefined ? targetCamId : selectedCameraId;
    const micId = targetMicId !== undefined ? targetMicId : selectedMicId;

    // Auto-detect whether any camera is physically available
    let hasAvailableCamera = true;
    if (!isMobileDevice) {
      try {
        const devList = await navigator.mediaDevices.enumerateDevices();
        const cams = devList.filter(d => d.kind === 'videoinput');
        if (cams.length === 0) hasAvailableCamera = false;
      } catch {}
    }

    const audioConstraints: boolean | MediaTrackConstraints = micId
      ? {
          deviceId: { exact: micId },
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      : { echoCancellation: true, noiseSuppression: true, autoGainControl: true };

    // If no camera is available on the system, acquire audio-only stream directly
    if (!hasAvailableCamera) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
        setLocalStream(stream);
        setIsVideoMuted(true);
        setIsRequestingMedia(false);
        enumerateMediaDevices();
        return stream;
      } catch {
        setMediaPermissionDenied(true);
        setIsRequestingMedia(false);
        return null;
      }
    }

    let videoConstraints: boolean | MediaTrackConstraints;
    if (camId) {
      videoConstraints = {
        deviceId: { exact: camId },
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 30 }
      };
    } else if (isMobileDevice) {
      videoConstraints = {
        facingMode: { ideal: targetFacing },
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 30 }
      };
    } else {
      videoConstraints = {
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 30 }
      };
    }

    try {
      // 1. Try ideal video & audio with chosen camera & mic
      const stream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints,
        audio: audioConstraints
      });
      setLocalStream(stream);
      setIsVideoMuted(false);
      if (lobbyVideoRef.current) {
        lobbyVideoRef.current.srcObject = stream;
      }
      setIsRequestingMedia(false);
      enumerateMediaDevices();
      return stream;
    } catch {
      // 2. Try simple video & audio without strict constraints
      try {
        const fallbackVideo = camId ? { deviceId: camId } : (isMobileDevice ? { facingMode: { ideal: targetFacing } } : true);
        const fallbackAudio = micId ? { deviceId: micId } : true;
        const stream = await navigator.mediaDevices.getUserMedia({
          video: fallbackVideo,
          audio: fallbackAudio
        });
        setLocalStream(stream);
        setIsVideoMuted(false);
        if (lobbyVideoRef.current) {
          lobbyVideoRef.current.srcObject = stream;
        }
        setIsRequestingMedia(false);
        enumerateMediaDevices();
        return stream;
      } catch {
        // 3. Fallback to audio-only if camera is not connected or in use
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
          setLocalStream(stream);
          setIsVideoMuted(true);
          setIsRequestingMedia(false);
          enumerateMediaDevices();
          return stream;
        } catch {
          // Camera & Mic blocked or permission denied
          setMediaPermissionDenied(true);
          setIsRequestingMedia(false);
          return null;
        }
      }
    }
  }, [facingMode, isMobileDevice, selectedCameraId, selectedMicId, enumerateMediaDevices]);

  // Physical hardware disconnect listener on video track
  useEffect(() => {
    if (!localStream) return;
    const vTrack = localStream.getVideoTracks()[0];
    if (vTrack) {
      const handleEnded = () => {
        enumerateMediaDevices();
        setIsVideoMuted(true);
        showToast('Camera disconnected • Audio-Only mode active');
      };
      vTrack.addEventListener('ended', handleEnded);
      return () => vTrack.removeEventListener('ended', handleEnded);
    }
  }, [localStream, enumerateMediaDevices]);

  // Flip mobile camera handler
  const handleFlipMobileCamera = async () => {
    if (isFlippingCamera) return;
    setIsFlippingCamera(true);
    const nextFacing = facingMode === 'user' ? 'environment' : 'user';
    try {
      if (localStream) {
        localStream.getVideoTracks().forEach(t => t.stop());
      }
      const stream = await acquireLobbyMedia(nextFacing);
      if (stream) {
        setFacingMode(nextFacing);
        webrtcGroupMeetingService.updateLocalStream(stream);
      }
    } finally {
      setIsFlippingCamera(false);
    }
  };

  // Select Camera Handler
  const handleSelectCamera = async (deviceId: string) => {
    setSelectedCameraId(deviceId);
    try {
      if (localStream) {
        localStream.getVideoTracks().forEach(t => t.stop());
      }
      const newStream = await acquireLobbyMedia(facingMode, deviceId, selectedMicId);
      if (newStream) {
        const videoTrack = newStream.getVideoTracks()[0];
        if (videoTrack) videoTrack.enabled = !isVideoMuted;
        const audioTrack = newStream.getAudioTracks()[0];
        if (audioTrack) audioTrack.enabled = !isMicMuted;

        webrtcGroupMeetingService.updateLocalStream(newStream);
        showToast('Camera switched successfully');
      }
    } catch {
      showToast('Could not switch camera');
    }
  };

  // Select Microphone Handler
  const handleSelectMic = async (deviceId: string) => {
    setSelectedMicId(deviceId);
    try {
      if (localStream) {
        localStream.getAudioTracks().forEach(t => t.stop());
      }
      const newStream = await acquireLobbyMedia(facingMode, selectedCameraId, deviceId);
      if (newStream) {
        const videoTrack = newStream.getVideoTracks()[0];
        if (videoTrack) videoTrack.enabled = !isVideoMuted;
        const audioTrack = newStream.getAudioTracks()[0];
        if (audioTrack) audioTrack.enabled = !isMicMuted;

        webrtcGroupMeetingService.updateLocalStream(newStream);
        showToast('Microphone switched successfully');
      }
    } catch {
      showToast('Could not switch microphone');
    }
  };

  // Acquire local camera/mic stream on mount for Lobby
  useEffect(() => {
    let streamInstance: MediaStream | null = null;
    acquireLobbyMedia().then(stream => {
      streamInstance = stream;
    });

    return () => {
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(t => t.stop());
        localStreamRef.current = null;
      }
      if (streamInstance) {
        streamInstance.getTracks().forEach(t => t.stop());
      }
      try {
        webrtcGroupMeetingService.disconnect();
      } catch {}
      if (callDurationRef.current) clearInterval(callDurationRef.current);
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    };
  }, []);

  // Sync lobby video ref when stream updates
  useEffect(() => {
    if (lobbyVideoRef.current && localStream) {
      lobbyVideoRef.current.srcObject = localStream;
    }
  }, [localStream, stage]);

  // QR Code generator when Info drawer or Invite Modal opens
  const meetingUrl = typeof window !== 'undefined'
    ? `${window.location.origin}${window.location.pathname}?meet=${activeRoomCode || roomCodeInput || 'meeting'}`
    : '';

  useEffect(() => {
    if ((activeDrawer === 'info' || showInviteModal) && qrCanvasRef.current && meetingUrl) {
      QRCode.toCanvas(qrCanvasRef.current, meetingUrl, {
        width: 180,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      }).catch(console.warn);
    }
  }, [activeDrawer, showInviteModal, meetingUrl]);

  // Setup WebRTC Group Meeting Callbacks
  useEffect(() => {
    webrtcGroupMeetingService.setCallbacks({
      onStatusChange: (newStatus) => {
        setConnectionStatus(newStatus);
        if (newStatus === 'connected') {
          if (!callDurationRef.current) {
            setCallDurationSec(0);
            callDurationRef.current = setInterval(() => {
              setCallDurationSec(prev => prev + 1);
            }, 1000);
          }
        } else if (newStatus === 'disconnected' || newStatus === 'failed') {
          if (callDurationRef.current) {
            clearInterval(callDurationRef.current);
            callDurationRef.current = null;
          }
        }
      },
      onParticipantsChange: (allParticipants) => {
        setParticipants([...allParticipants]);
      },
      onChatMessage: (newMsg) => {
        setChatMessages(prev => [...prev, newMsg]);
        playTone('message');
        if (activeDrawer !== 'chat') {
          setUnreadChatCount(prev => prev + 1);
        }
        setTimeout(() => {
          chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      },
      onActiveSpeakerChange: (speakerId) => {
        setActiveSpeakerId(speakerId);
      },
      onScreenShareChange: (isSharing, sharerId) => {
        const localId = webrtcGroupMeetingService.getLocalParticipantId();
        if (sharerId === localId) {
          setIsScreenSharing(isSharing);
        }
        if (isSharing && sharerId) {
          setPinnedParticipantId(sharerId);
          if (sharerId !== localId) {
            showToast('A participant is presenting screen');
          }
        } else {
          setPinnedParticipantId(curr => (curr === sharerId ? null : curr));
        }
      },
      onUserJoined: (name) => {
        showToast(`${name} joined the meeting`);
        playTone('join');
      },
      onUserLeft: (name) => {
        showToast(`${name} left the meeting`);
        playTone('leave');
      },
      onError: (errMsg) => {
        showToast(`Error: ${errMsg}`);
      }
    });
  }, [activeDrawer, pinnedParticipantId]);

  // Save display name to localStorage
  const handleNameChange = (val: string) => {
    setUserName(val);
    localStorage.setItem('meetsplit_user_name', val);
  };

  // Toggle Microphone
  const toggleMic = () => {
    const nextState = !isMicMuted;
    setIsMicMuted(nextState);
    if (stage === 'in-meeting') {
      webrtcGroupMeetingService.muteAudio(nextState);
    } else if (localStream) {
      localStream.getAudioTracks().forEach(t => { t.enabled = !nextState; });
    }
  };

  // Toggle Camera
  const toggleVideo = () => {
    const nextState = !isVideoMuted;
    setIsVideoMuted(nextState);
    if (stage === 'in-meeting') {
      webrtcGroupMeetingService.muteVideo(nextState);
    } else if (localStream) {
      localStream.getVideoTracks().forEach(t => { t.enabled = !nextState; });
    }
  };

  // Toggle Screen Sharing
  const toggleScreenShare = async () => {
    if (isScreenSharing) {
      webrtcGroupMeetingService.stopScreenShare();
      setIsScreenSharing(false);
      showToast('Screen sharing stopped');
    } else {
      try {
        await webrtcGroupMeetingService.startScreenShare();
        setIsScreenSharing(true);
        showToast('Screen sharing is now active');
      } catch (err: any) {
        setIsScreenSharing(false);
        if (err.name !== 'NotAllowedError' && err.name !== 'AbortError') {
          showToast(`Screen share failed: ${err.message || 'Permission denied'}`);
        }
      }
    }
  };

  // Toggle Raise Hand
  const handleRaiseHand = () => {
    const raised = webrtcGroupMeetingService.toggleRaiseHand();
    setIsHandRaised(raised);
    if (raised) {
      playTone('hand');
      showToast('You raised your hand ✋');
    }
  };

  // Floating Emoji Reaction
  const handleSendReaction = (emoji: string) => {
    confetti({
      particleCount: 28,
      spread: 60,
      origin: { y: 0.85, x: 0.5 }
    });
    webrtcGroupMeetingService.sendChatMessage(`Reacted ${emoji}`);
  };

  // Host Action: Create Room
  const handleCreateMeeting = async () => {
    if (!userName.trim()) {
      showToast('Please enter your name');
      return;
    }

    const generated = webrtcGroupMeetingService.getRoomCode() || WebRTCGroupMeetingService.generateRoomCode();
    setActiveRoomCode(generated);

    let stream = localStream;
    if (!stream) {
      stream = await acquireLobbyMedia();
      if (!stream) {
        showToast('Please grant camera or microphone access to host');
        return;
      }
    }

    try {
      setStage('in-meeting');
      await webrtcGroupMeetingService.createRoom(generated, userName, stream);
      showToast(`Group Meeting room created: ${generated}`);
    } catch (err: any) {
      setStage('lobby');
      showToast(err.message || 'Failed to create room');
    }
  };

  // Guest Action: Join Room
  const handleJoinMeeting = async () => {
    if (!userName.trim()) {
      showToast('Please enter your name');
      return;
    }
    const cleanCode = roomCodeInput.trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
    if (!cleanCode) {
      showToast('Please enter a valid meeting code');
      return;
    }

    setActiveRoomCode(cleanCode);

    let stream = localStream;
    if (!stream) {
      stream = await acquireLobbyMedia();
      if (!stream) {
        showToast('Please grant camera or microphone access to join');
        return;
      }
    }

    try {
      setStage('in-meeting');
      await webrtcGroupMeetingService.joinRoom(cleanCode, userName, stream);
      showToast(`Joined meeting room: ${cleanCode}`);
    } catch (err: any) {
      setStage('lobby');
      showToast(err.message || 'Failed to join room');
    }
  };

  // Leave / Disconnect Meeting
  const handleLeaveMeeting = () => {
    webrtcGroupMeetingService.disconnect();
    if (callDurationRef.current) clearInterval(callDurationRef.current);
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    setIsRecording(false);
    setRecordingDurationSec(0);
    setCallDurationSec(0);
    setPinnedParticipantId(null);
    setIsScreenSharing(false);
    setIsHandRaised(false);
    setStage('lobby');
    showToast('You left the meeting');
  };

  // Send In-Meeting Chat
  const handleSendChat = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!chatInput.trim()) return;
    const sent = webrtcGroupMeetingService.sendChatMessage(chatInput.trim());
    if (sent) {
      setChatMessages(prev => [...prev, sent]);
      setChatInput('');
      setTimeout(() => {
        chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 50);
    }
  };

  // Toggle Meeting Recording
  const handleToggleRecording = async () => {
    if (isRecording) {
      // Stop recording
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      const res = await webrtcGroupMeetingService.stopCompositeRecording();
      setIsRecording(false);
      setRecordingDurationSec(0);
      showToast('Meeting recorded successfully as a Single Video file (No separate participant tracks)');

      if (res.blob && res.blob.size > 0) {
        const filename = `GroupMeeting-${activeRoomCode}-${Date.now()}.webm`;
        downloadBlob(res.blob, filename);

        let initialThumb = '';
        try {
          initialThumb = await generateThumbnailFromBlob(res.blob);
        } catch (e) {
          console.warn('Failed to generate thumbnail from blob', e);
        }

        if (onMeetingRecorded) {
          onMeetingRecorded(res.blob, res.durationSec, `Group Meeting (${activeRoomCode})`, initialThumb);
        }
      }

    } else {
      // Start recording
      try {
        await webrtcGroupMeetingService.startCompositeRecording();
        setIsRecording(true);
        setRecordingDurationSec(0);
        recordingTimerRef.current = setInterval(() => {
          setRecordingDurationSec(prev => prev + 1);
        }, 1000);
        showToast('Single Meeting Recording started • Capturing all participants in one unified video');
      } catch (err) {
        showToast('Failed to start recording');
      }
    }
  };

  // Copy helper
  const handleCopyLink = () => {
    navigator.clipboard.writeText(meetingUrl);
    setCopiedLink(true);
    showToast('Meeting link copied to clipboard!');
    setTimeout(() => setCopiedLink(false), 2500);
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(activeRoomCode || roomCodeInput);
    setCopiedCode(true);
    showToast('Meeting code copied!');
    setTimeout(() => setCopiedCode(false), 2500);
  };

  // ==========================================
  // RENDER: PRE-JOIN GREEN ROOM / LOBBY
  // ==========================================
  if (stage === 'lobby') {
    return (
      <div className="relative w-screen h-[100dvh] bg-[#0B1020] text-slate-100 flex flex-col overflow-hidden select-none font-sans">
        {/* Toast Banner */}
        {toastMessage && (
          <div className="fixed top-3 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-xl bg-black/85 backdrop-blur-md border border-purple-500/50 text-white shadow-2xl text-xs font-semibold flex items-center gap-2 animate-in fade-in slide-in-from-top-2">
            <Sparkles className="w-3.5 h-3.5 text-purple-400" />
            <span>{toastMessage}</span>
          </div>
        )}

        {/* --- TOP APP BAR --- */}
        <header className="shrink-0 px-3 sm:px-5 py-2 bg-[#11172A]/95 backdrop-blur-md border-b border-slate-800/80 flex items-center justify-between z-20 gap-2">
          <div className="flex items-center gap-2 shrink-0">
            {!isMobileGuest && (
              <button
                onClick={() => onNavigate('dashboard')}
                className="p-1.5 rounded-lg bg-slate-800/80 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors cursor-pointer mr-0.5"
                title="Return to Dashboard"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-md shadow-blue-500/20 text-white shrink-0">
              <Users className="w-3.5 h-3.5 animate-pulse" />
            </div>
            <div>
              <h1 className="text-xs sm:text-sm font-bold tracking-tight text-white flex items-center gap-1.5 leading-none">
                <span>MeetSplit AI</span>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                  Guest
                </span>
              </h1>
            </div>
          </div>

          {/* Tab Switcher in Center of Header */}
          {onSwitchGuestTab ? (
            <div className="flex items-center p-0.5 rounded-xl bg-slate-900/90 border border-slate-800 shadow-inner">
              <button
                type="button"
                onClick={() => onSwitchGuestTab('group')}
                className="py-1 px-2.5 sm:px-3 rounded-lg font-bold text-[11px] sm:text-xs flex items-center justify-center gap-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-sm cursor-pointer"
              >
                <Users className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                <span>Group Meeting</span>
              </button>
              <button
                type="button"
                onClick={() => onSwitchGuestTab('podcast')}
                className="py-1 px-2.5 sm:px-3 rounded-lg font-bold text-[11px] sm:text-xs flex items-center justify-center gap-1.5 text-slate-400 hover:text-slate-200 transition-all cursor-pointer"
              >
                <Radio className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                <span>Podcast Guest</span>
              </button>
            </div>
          ) : (
            <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-800/60 border border-slate-700/60 text-[10px] text-slate-300">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              <span>Direct P2P Mesh Encrypted</span>
            </div>
          )}

          {/* Camera Status / Switcher on Right */}
          <div className="flex items-center gap-2">
            <PwaInstallButton variant="compact" />
            {isMobileDevice ? (
              <button
                type="button"
                onClick={handleFlipMobileCamera}
                disabled={isFlippingCamera}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-800/80 hover:bg-slate-700 active:scale-95 border border-slate-700 text-[11px] font-semibold text-slate-200 transition-all cursor-pointer shrink-0"
                title="Flip Camera (Front/Back)"
              >
                <SwitchCamera className={`w-3.5 h-3.5 text-indigo-400 ${isFlippingCamera ? 'animate-spin' : ''}`} />
                <span>{facingMode === 'user' ? 'Front' : 'Back'}</span>
              </button>
            ) : (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-800/60 border border-slate-700/60 text-[10px] sm:text-[11px] text-slate-300 shrink-0">
                {localStream ? (
                  videoDevices.length > 0 ? (
                    <>
                      <Camera className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                      <span className="truncate max-w-[100px] sm:max-w-[140px] text-emerald-300 font-semibold">Camera & Mic Live</span>
                    </>
                  ) : (
                    <>
                      <Mic className="w-3.5 h-3.5 text-amber-400 animate-pulse shrink-0" />
                      <span className="truncate max-w-[100px] sm:max-w-[140px] text-amber-300 font-medium">Audio Only</span>
                    </>
                  )
                ) : (
                  <span className="text-amber-400 font-medium">Ready to Join</span>
                )}
              </div>
            )}
          </div>
        </header>

        {/* =========================================================================
            MAIN LOBBY VIEWPORT (ZERO-SCROLL BALANCED 2-COLUMN STUDIO LOBBY)
           ========================================================================= */}
        <main className="flex-1 min-h-0 overflow-hidden px-2.5 sm:px-6 py-2 flex flex-col justify-center items-center w-full max-w-5xl mx-auto scrollbar-none no-scrollbar">
          <div className="w-full grid grid-cols-1 sm:grid-cols-12 gap-2.5 sm:gap-4 lg:gap-6 items-center my-auto">
            {/* --- LEFT COLUMN: CAMERA PREVIEW + MEDIA HARDWARE SELECTORS --- */}
            <div className="sm:col-span-7 flex flex-col gap-2 justify-center">
              {/* 1. CAMERA PREVIEW CARD */}
              <div className="relative rounded-2xl bg-black border border-slate-800 shadow-xl overflow-hidden aspect-video sm:aspect-[16/10] max-h-[190px] sm:max-h-[280px] lg:max-h-[320px] flex items-center justify-center">
                {!localStream ? (
                  <div className="flex flex-col items-center justify-center p-4 text-center space-y-2 w-full h-full bg-slate-950">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/20 text-blue-400 flex items-center justify-center border border-blue-500/30">
                      <Video className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-xs font-bold text-white">Camera & Microphone Access</h3>
                      <p className="text-[11px] text-slate-400 mt-0.5 max-w-xs">
                        {mediaPermissionDenied
                          ? 'Permission was denied. Please allow camera and microphone access in browser.'
                          : 'Grant permission to preview video & audio before entering the meeting.'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => acquireLobbyMedia()}
                      disabled={isRequestingMedia}
                      className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-[11px] shadow-lg shadow-indigo-600/30 transition-all active:scale-95 cursor-pointer flex items-center gap-1.5"
                    >
                      <Sparkles className="w-3.5 h-3.5 text-cyan-200" />
                      <span>{isRequestingMedia ? 'Requesting...' : 'ALLOW CAMERA & MIC'}</span>
                    </button>
                  </div>
                ) : isVideoMuted || !localStream.getVideoTracks().length ? (
                  <div className="flex flex-col items-center justify-center p-4 text-center space-y-2 w-full h-full bg-slate-950/90 animate-in fade-in">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center text-xl sm:text-2xl font-black text-white shadow-xl shadow-blue-600/30 ring-4 ring-indigo-500/20">
                      {userName.slice(0, 2).toUpperCase() || 'GU'}
                    </div>
                    <div>
                      <h3 className="text-xs sm:text-sm font-bold text-white flex items-center justify-center gap-1.5">
                        <Mic className="w-3.5 h-3.5 text-emerald-400 animate-pulse" />
                        <span>{videoDevices.length === 0 ? 'No Camera Detected (Audio-Only Mode)' : 'Camera is Turned Off'}</span>
                      </h3>
                      <p className="text-[10px] sm:text-xs text-slate-400 mt-0.5 max-w-xs">
                        {videoDevices.length === 0
                          ? 'Microphone is auto-detected and live. Connect webcam anytime.'
                          : 'Your microphone is live and working'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <video
                      ref={lobbyVideoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover scale-x-[-1]"
                    />

                    {/* Top Status Pill */}
                    <div className="absolute top-2 left-2 flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-black/60 backdrop-blur-md border border-white/10 text-[10px] text-white font-semibold">
                      <span className={`w-1.5 h-1.5 rounded-full ${localStream ? (videoDevices.length === 0 ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400 animate-pulse') : 'bg-rose-400'}`} />
                      <span className="truncate max-w-[130px] sm:max-w-[160px]">
                        {videoDevices.length === 0 ? 'Audio-only Mode' : 'Camera & Mic Live'}
                      </span>
                    </div>

                    {/* Bottom In-Preview Controls */}
                    <div className="absolute bottom-2.5 inset-x-0 flex items-center justify-center gap-2 sm:gap-3 z-10 px-3">
                      <button
                        type="button"
                        onClick={toggleMic}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold shadow-lg backdrop-blur-md transition-all active:scale-95 cursor-pointer ${
                          isMicMuted
                            ? 'bg-red-600/90 text-white border border-red-500'
                            : 'bg-black/60 hover:bg-black/80 text-emerald-300 border border-emerald-500/40'
                        }`}
                      >
                        {isMicMuted ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
                        <span>{isMicMuted ? 'Muted' : 'Mic Live'}</span>
                      </button>

                      <button
                        type="button"
                        onClick={toggleVideo}
                        disabled={videoDevices.length === 0}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold shadow-lg backdrop-blur-md transition-all active:scale-95 cursor-pointer ${
                          videoDevices.length === 0
                            ? 'bg-slate-800/80 text-slate-500 border border-slate-700 cursor-not-allowed'
                            : isVideoMuted
                            ? 'bg-red-600/90 text-white border border-red-500'
                            : 'bg-black/60 hover:bg-black/80 text-indigo-300 border border-indigo-500/40'
                        }`}
                      >
                        {isVideoMuted ? <VideoOff className="w-3.5 h-3.5" /> : <Video className="w-3.5 h-3.5" />}
                        <span>{videoDevices.length === 0 ? 'No Camera' : isVideoMuted ? 'Cam Off' : 'Cam Live'}</span>
                      </button>

                      {isMobileDevice && (
                        <button
                          type="button"
                          onClick={handleFlipMobileCamera}
                          disabled={isFlippingCamera}
                          className="p-1.5 rounded-xl bg-black/60 hover:bg-black/80 border border-white/15 text-white active:scale-95 transition-all cursor-pointer"
                          title="Flip Camera (Front/Back)"
                        >
                          <SwitchCamera className={`w-3.5 h-3.5 text-white ${isFlippingCamera ? 'animate-spin' : ''}`} />
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* 1.1 DESKTOP DEVICE SELECTORS (Auto-Detected Media Hardware - Desktop PC Only) */}
              {!isMobileDevice && (
                <div className="grid grid-cols-2 gap-2 p-2 rounded-xl bg-slate-900/90 border border-slate-800 text-xs shadow-md">
                  {/* Camera Selector */}
                  <div className="space-y-0.5">
                    <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400 flex items-center justify-between">
                      <span className="flex items-center gap-1">
                        <Video className="w-3 h-3 text-indigo-400" />
                        <span>Camera</span>
                      </span>
                      <span className={`text-[8px] font-mono px-1 py-0.2 rounded ${videoDevices.length > 0 ? 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/20' : 'text-amber-400 bg-amber-500/10 border border-amber-500/20'}`}>
                        {videoDevices.length > 0 ? `${videoDevices.length} Auto` : 'None'}
                      </span>
                    </label>
                    <select
                      value={selectedCameraId}
                      onChange={(e) => handleSelectCamera(e.target.value)}
                      disabled={videoDevices.length === 0}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 text-[11px] text-slate-200 focus:outline-none focus:border-indigo-500 truncate cursor-pointer disabled:opacity-50"
                    >
                      {videoDevices.length === 0 ? (
                        <option value="">No Camera Detected</option>
                      ) : (
                        videoDevices.map((d) => (
                          <option key={d.deviceId} value={d.deviceId}>
                            {d.label}
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  {/* Microphone Selector */}
                  <div className="space-y-0.5">
                    <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400 flex items-center justify-between">
                      <span className="flex items-center gap-1">
                        <Mic className="w-3 h-3 text-emerald-400" />
                        <span>Microphone</span>
                      </span>
                      <span className={`text-[8px] font-mono px-1 py-0.2 rounded ${audioDevices.length > 0 ? 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/20' : 'text-rose-400 bg-rose-500/10 border border-rose-500/20'}`}>
                        {audioDevices.length > 0 ? `${audioDevices.length} Auto` : 'None'}
                      </span>
                    </label>
                    <select
                      value={selectedMicId}
                      onChange={(e) => handleSelectMic(e.target.value)}
                      disabled={audioDevices.length === 0}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 text-[11px] text-slate-200 focus:outline-none focus:border-indigo-500 truncate cursor-pointer disabled:opacity-50"
                    >
                      {audioDevices.length === 0 ? (
                        <option value="">No Microphone Detected</option>
                      ) : (
                        audioDevices.map((d) => (
                          <option key={d.deviceId} value={d.deviceId}>
                            {d.label}
                          </option>
                        ))
                      )}
                    </select>

                    {/* Mic volume bar */}
                    <div className="pt-0.5 flex items-center gap-1.5">
                      <div className="flex-1 h-1 bg-slate-800 rounded-full overflow-hidden border border-slate-700">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-500 via-teal-400 to-indigo-500 transition-all duration-75"
                          style={{ width: `${isMicMuted ? 0 : micVolumeLevel}%` }}
                        />
                      </div>
                      <span className="text-[8px] font-mono text-slate-400 shrink-0">
                        {isMicMuted ? 'Muted' : `${micVolumeLevel}%`}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* --- RIGHT COLUMN: JOIN / HOST CARD + SECURITY BANNER --- */}
            <div className="sm:col-span-5 flex flex-col gap-2 justify-center">
              {/* 2. JOIN / CREATE CARD */}
              <div className="p-3 sm:p-4 rounded-2xl bg-slate-900/90 border border-slate-800 shadow-xl space-y-2.5">
                {/* Display Name Input */}
                <div>
                  <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400 block mb-0.5">
                    Your Display Name
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={userName}
                      onChange={(e) => handleNameChange(e.target.value)}
                      placeholder="Your Name (e.g. Alex)"
                      className="w-full px-3 py-1.5 rounded-lg bg-slate-800/70 border border-slate-700/80 text-xs font-semibold text-slate-200 focus:outline-none focus:border-blue-500 pr-8"
                    />
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center text-[10px] font-bold text-white">
                      {userName.charAt(0).toUpperCase() || 'U'}
                    </div>
                  </div>
                </div>

                {/* Mode Tabs: Create vs Join */}
                <div className="flex items-center p-0.5 rounded-xl bg-slate-800/80 border border-slate-700/60">
                  <button
                    type="button"
                    onClick={() => setLobbyAction('join')}
                    className={`flex-1 py-1 text-[11px] font-bold rounded-lg transition-all text-center cursor-pointer ${
                      lobbyAction === 'join'
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    🔑 Join Meeting (Code)
                  </button>
                  <button
                    type="button"
                    onClick={() => setLobbyAction('create')}
                    className={`flex-1 py-1 text-[11px] font-bold rounded-lg transition-all text-center cursor-pointer ${
                      lobbyAction === 'create'
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    🚀 Create Meeting (Host)
                  </button>
                </div>

                {lobbyAction === 'join' ? (
                  <div className="space-y-2">
                    <div>
                      <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400 block mb-0.5">
                        Meeting Room Code or URL
                      </label>
                      <input
                        type="text"
                        value={roomCodeInput}
                        onChange={(e) => setRoomCodeInput(e.target.value)}
                        placeholder="e.g. room-alpha-92"
                        className="w-full px-3 py-1.5 sm:py-2 rounded-xl bg-slate-800/90 border-2 border-blue-500/40 focus:border-blue-500 text-center text-base sm:text-lg font-black font-mono tracking-wider text-white focus:outline-none transition-colors"
                      />
                    </div>

                    <button
                      type="button"
                      onClick={handleJoinMeeting}
                      disabled={!roomCodeInput.trim()}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-600 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-xs uppercase tracking-wider shadow-lg shadow-blue-600/30 active:scale-[0.98] transition-all cursor-pointer"
                    >
                      <Users className="w-4 h-4" />
                      <span>JOIN GROUP MEETING NOW</span>
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="p-2.5 rounded-xl bg-blue-950/40 border border-blue-500/20 text-xs text-blue-200/90 space-y-1">
                      <p className="font-bold flex items-center gap-1.5 text-blue-300 text-[11px]">
                        <Sparkles className="w-3.5 h-3.5" /> Instant Multi-User Conference
                      </p>
                      <p className="text-[10px] text-slate-300 leading-snug">
                        Create a room instantly. Team members can join from browser, mobile phone, or desktop app.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={handleCreateMeeting}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 hover:brightness-110 active:scale-[0.98] text-white font-bold text-xs uppercase tracking-wider shadow-lg shadow-purple-600/30 transition-all cursor-pointer"
                    >
                      <Video className="w-4 h-4" />
                      <span>START NEW GROUP MEETING</span>
                    </button>
                  </div>
                )}
              </div>

              {/* 3. ZERO STORAGE & PRIVACY BANNER */}
              <div className="p-2 rounded-xl bg-slate-900/60 border border-slate-800/80 text-[11px] flex items-center gap-2">
                <div className="p-1 rounded-lg bg-emerald-500/15 text-emerald-400 shrink-0">
                  <ShieldCheck className="w-3.5 h-3.5" />
                </div>
                <div className="text-slate-300 text-[10px] sm:text-[11px] leading-tight">
                  <strong className="text-white block font-semibold">Direct P2P Mesh Encrypted</strong>
                  <span className="text-slate-400">Zero Cloud Storage. Live Screen Share & Audio Visualizer.</span>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ==========================================
  // RENDER: ACTIVE GROUP MEETING ROOM (STAGE)
  // ==========================================
  const allTiles = participants;
  const pinnedParticipant = pinnedParticipantId
    ? participants.find(p => p.id === pinnedParticipantId)
    : null;

  // Grid layout class based on count - full height row stretching
  const getGridClasses = (count: number) => {
    if (count <= 1) return 'grid-cols-1 grid-rows-1';
    if (count === 2) return 'grid-cols-1 md:grid-cols-2 grid-rows-2 md:grid-rows-1';
    if (count <= 4) return 'grid-cols-2 grid-rows-2';
    if (count <= 6) return 'grid-cols-2 md:grid-cols-3 grid-rows-3 md:grid-rows-2';
    return 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4';
  };

  return (
    <div
      ref={meetingRoomContainerRef}
      className={`fixed inset-0 w-screen h-[100dvh] flex flex-col bg-[#0a0f1d] text-white overflow-hidden select-none z-50 ${
        isFullscreen ? 'w-screen h-screen' : ''
      }`}
    >
      {/* Toast Notification Banner */}
      {toastMessage && (
        <div className="fixed top-5 left-1/2 -translate-x-1/2 z-50 px-5 py-2.5 rounded-2xl bg-black/85 backdrop-blur-md border border-purple-500/50 text-white shadow-2xl text-xs md:text-sm font-semibold flex items-center gap-2 animate-in fade-in slide-in-from-top-4">
          <Sparkles className="w-4 h-4 text-purple-400" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Top Meeting Header Bar */}
      <div className="h-14 px-5 bg-black/40 backdrop-blur-xl border-b border-white/10 flex items-center justify-between shrink-0 z-20">
        {/* Left: Room Code & Security Badge */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/5 border border-white/10">
            <span className="text-xs font-mono font-bold text-white tracking-wider">{activeRoomCode}</span>
            <button
              onClick={handleCopyCode}
              className="p-1 hover:bg-white/10 rounded-lg text-white/60 hover:text-white transition-colors cursor-pointer"
              title="Copy Room Code"
            >
              {copiedCode ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
          </div>

          <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-[11px] text-emerald-300 font-medium">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>Encrypted Mesh</span>
          </div>

          {/* Recording Pill if Active */}
          {isRecording && (
            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/20 border border-red-500/40 text-red-300 text-xs font-bold animate-pulse">
              <span className="w-2 h-2 rounded-full bg-red-500" />
              <span>REC {formatDuration(recordingDurationSec)}</span>
            </div>
          )}
        </div>

        {/* Center: Running Timer */}
        <div className="font-mono text-sm font-bold text-white/80 tracking-wider">
          {formatDuration(callDurationSec)}
        </div>

        {/* Right: Fullscreen, Devices, Quick Share & Participant count */}
        <div className="flex items-center gap-2">
          <PwaInstallButton variant="compact" />
          {/* Full Screen Toggle (Room Only) */}
          <button
            onClick={toggleFullscreen}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 text-white text-xs font-semibold transition-all cursor-pointer"
            title={isFullscreen ? 'Exit Full Screen' : 'Full Screen (Room Only)'}
          >
            {isFullscreen ? <Minimize2 className="w-3.5 h-3.5 text-indigo-400" /> : <Maximize2 className="w-3.5 h-3.5 text-indigo-400" />}
            <span className="hidden sm:inline">{isFullscreen ? 'Exit Fullscreen' : 'Full Screen'}</span>
          </button>

          {/* Audio & Video Devices */}
          <button
            onClick={() => setShowDeviceSettingsModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 text-white text-xs font-semibold transition-all cursor-pointer"
            title="Audio & Video Devices"
          >
            <Settings className="w-3.5 h-3.5 text-purple-400" />
            <span className="hidden sm:inline">Devices</span>
          </button>

          <button
            onClick={() => setShowInviteModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-purple-600/30 hover:bg-purple-600/50 border border-purple-500/40 text-purple-200 text-xs font-semibold transition-all cursor-pointer"
          >
            <Share2 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Invite People</span>
          </button>
        </div>
      </div>

      {/* Center Viewport: Video Grid Stage + Slide-in Drawer */}
      <div className="flex-1 min-h-0 flex overflow-hidden relative w-full h-full">
        {/* Main Video Presentation Grid */}
        <div className="flex-1 min-h-0 p-2 sm:p-3 md:p-4 flex flex-col justify-center overflow-hidden w-full h-full">
          {pinnedParticipant ? (
            /* PINNED / SPOTLIGHT VIEW */
            <div className="flex-1 min-h-0 flex flex-col gap-3 h-full w-full">
              {/* Central Focused View */}
              <div className="flex-1 min-h-0 relative rounded-2xl md:rounded-3xl overflow-hidden bg-black/60 border border-white/10 shadow-2xl flex items-center justify-center w-full h-full">
                <ParticipantVideoTile
                  participant={pinnedParticipant}
                  isPinned={true}
                  onTogglePin={() => setPinnedParticipantId(null)}
                />
                <button
                  onClick={() => setPinnedParticipantId(null)}
                  className="absolute top-4 right-4 px-3 py-1.5 rounded-xl bg-black/70 hover:bg-black/90 backdrop-blur-md text-xs font-bold text-white border border-white/20 flex items-center gap-1.5 shadow-lg transition-all"
                >
                  <PinOff className="w-3.5 h-3.5" />
                  <span>Unpin</span>
                </button>
              </div>

              {/* Horizontal Filmstrip of remaining participants */}
              <div className="h-24 md:h-28 flex items-center gap-2 md:gap-3 overflow-x-auto pb-1 shrink-0 w-full">
                {participants.map((p) => (
                  <div
                    key={p.id}
                    onClick={() => setPinnedParticipantId(p.id)}
                    className={`h-full aspect-video rounded-xl md:rounded-2xl overflow-hidden bg-black/40 border cursor-pointer transition-all relative shrink-0 ${
                      p.id === pinnedParticipantId
                        ? 'border-purple-500 ring-2 ring-purple-500/50'
                        : 'border-white/10 hover:border-white/30'
                    }`}
                  >
                    <ParticipantVideoTile participant={p} isThumbnail={true} />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* DYNAMIC RESPONSIVE GALLERY GRID - STRETCH TO 100% FULLSCREEN */
            <div className={`grid gap-2 sm:gap-3 md:gap-4 w-full h-full min-h-0 flex-1 ${getGridClasses(allTiles.length)}`}>
              {allTiles.map((participant) => (
                <div
                  key={participant.id}
                  className={`relative w-full h-full min-h-0 rounded-2xl md:rounded-3xl overflow-hidden bg-black/60 shadow-xl flex items-center justify-center group transition-all duration-300 ${
                    participant.id === activeSpeakerId
                      ? 'border-2 border-emerald-400 ring-4 ring-emerald-500/40 shadow-[0_0_35px_rgba(16,185,129,0.45)]'
                      : 'border border-white/10'
                  }`}
                >
                  <ParticipantVideoTile
                    participant={participant}
                    isSpeaking={participant.id === activeSpeakerId}
                    onTogglePin={() => setPinnedParticipantId(participant.id)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right Side Drawer (Chat / People / Info) */}
        {activeDrawer !== 'none' && (
          <div className="w-80 md:w-96 bg-[#0e1424] border-l border-white/10 flex flex-col shrink-0 z-30 shadow-2xl animate-in slide-in-from-right duration-200">
            {/* Drawer Header with Tabs */}
            <div className="p-3 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => { setActiveDrawer('chat'); setUnreadChatCount(0); }}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                    activeDrawer === 'chat'
                      ? 'bg-purple-600/30 text-purple-300 border border-purple-500/40'
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  <span>Chat</span>
                  {unreadChatCount > 0 && (
                    <span className="w-4 h-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
                      {unreadChatCount}
                    </span>
                  )}
                </button>

                <button
                  onClick={() => setActiveDrawer('people')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                    activeDrawer === 'people'
                      ? 'bg-purple-600/30 text-purple-300 border border-purple-500/40'
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  <Users className="w-3.5 h-3.5" />
                  <span>People ({participants.length})</span>
                </button>

                <button
                  onClick={() => setActiveDrawer('info')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                    activeDrawer === 'info'
                      ? 'bg-purple-600/30 text-purple-300 border border-purple-500/40'
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  <Info className="w-3.5 h-3.5" />
                  <span>Info</span>
                </button>
              </div>

              <button
                onClick={() => setActiveDrawer('none')}
                className="p-1 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors"
              >
                ✕
              </button>
            </div>

            {/* Drawer Body */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* TAB: CHAT */}
              {activeDrawer === 'chat' && (
                <div className="flex-1 flex flex-col overflow-hidden">
                  <div className="flex-1 p-4 overflow-y-auto space-y-3">
                    {chatMessages.length === 0 ? (
                      <div className="h-full flex flex-col items-center justify-center text-center p-6 text-white/40">
                        <MessageSquare className="w-8 h-8 mb-2 opacity-50" />
                        <p className="text-xs font-semibold">No messages yet</p>
                        <p className="text-[11px] text-white/30">Send a message to everyone in the room</p>
                      </div>
                    ) : (
                      chatMessages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex flex-col ${msg.isLocal ? 'items-end' : 'items-start'}`}
                        >
                          <div className="flex items-center gap-1.5 text-[10px] text-white/50 mb-1">
                            <span className="font-bold text-white/80">{msg.isLocal ? 'You' : msg.senderName}</span>
                            <span>•</span>
                            <span>{msg.timestamp}</span>
                          </div>
                          <div
                            className={`px-3.5 py-2 rounded-2xl text-xs max-w-[85%] break-words ${
                              msg.isLocal
                                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-tr-none'
                                : 'bg-white/10 text-white/90 rounded-tl-none border border-white/10'
                            }`}
                          >
                            {msg.text}
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={chatBottomRef} />
                  </div>

                  {/* Chat Input Bar */}
                  <form onSubmit={handleSendChat} className="p-3 border-t border-white/10 bg-black/30 flex items-center gap-2">
                    <input
                      type="text"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      placeholder="Send a message to everyone..."
                      className="flex-1 px-3.5 py-2 rounded-xl bg-white/5 border border-white/10 text-xs text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-purple-500"
                    />
                    <button
                      type="submit"
                      disabled={!chatInput.trim()}
                      className="p-2 rounded-xl bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white transition-all cursor-pointer"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </form>
                </div>
              )}

              {/* TAB: PEOPLE */}
              {activeDrawer === 'people' && (
                <div className="flex-1 p-4 overflow-y-auto space-y-3">
                  <div className="flex items-center justify-between pb-2 border-b border-white/10">
                    <span className="text-xs font-bold text-white/70">In this call ({participants.length})</span>
                    {webrtcGroupMeetingService.getParticipants().find(p => p.isLocal && p.isHost) && (
                      <button
                        onClick={() => webrtcGroupMeetingService.hostMuteAll()}
                        className="text-[11px] font-semibold text-red-400 hover:text-red-300"
                      >
                        Mute All
                      </button>
                    )}
                  </div>

                  <div className="space-y-2">
                    {participants.map((p) => (
                      <div
                        key={p.id}
                        className="flex items-center justify-between p-2.5 rounded-2xl bg-white/5 border border-white/5 hover:border-white/10 transition-all"
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className={`w-8 h-8 rounded-full bg-gradient-to-tr ${p.avatarBg || 'from-purple-600 to-indigo-600'} flex items-center justify-center text-xs font-bold text-white shrink-0 shadow-md`}>
                            {p.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-white truncate flex items-center gap-1.5">
                              {p.name}
                              {p.isLocal && <span className="text-[10px] text-white/40">(You)</span>}
                              {p.isHost && (
                                <span className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-purple-500/20 text-purple-300 border border-purple-500/40">
                                  Host
                                </span>
                              )}
                            </p>
                            {p.handRaised && (
                              <span className="text-[10px] text-amber-300 flex items-center gap-1">
                                ✋ Raised hand
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-1 text-white/60">
                          {p.audioMuted ? (
                            <MicOff className="w-3.5 h-3.5 text-red-400" />
                          ) : (
                            <Mic className="w-3.5 h-3.5 text-emerald-400" />
                          )}
                          {p.videoOff ? (
                            <VideoOff className="w-3.5 h-3.5 text-red-400" />
                          ) : (
                            <Video className="w-3.5 h-3.5 text-white/60" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => setShowInviteModal(true)}
                    className="w-full mt-4 py-2.5 rounded-xl bg-purple-600/30 hover:bg-purple-600/50 border border-purple-500/40 text-purple-200 text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <Share2 className="w-3.5 h-3.5" />
                    <span>Invite More People</span>
                  </button>
                </div>
              )}

              {/* TAB: INFO */}
              {activeDrawer === 'info' && (
                <div className="flex-1 p-5 overflow-y-auto space-y-5">
                  <div>
                    <h3 className="text-xs font-bold text-white/70 uppercase tracking-wider mb-2">Joining Info</h3>
                    <p className="text-xs text-white/50 mb-3">Share this link or code with guests so they can join directly:</p>

                    <div className="p-3 rounded-2xl bg-black/40 border border-white/10 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-mono font-bold text-purple-300">{activeRoomCode}</span>
                        <button
                          onClick={handleCopyCode}
                          className="px-2 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-[11px] font-semibold text-white flex items-center gap-1 cursor-pointer"
                        >
                          {copiedCode ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                          <span>{copiedCode ? 'Copied' : 'Copy Code'}</span>
                        </button>
                      </div>

                      <div className="h-px bg-white/10 my-2" />

                      <div className="flex items-center justify-between">
                        <span className="text-[11px] text-white/60 truncate max-w-[180px]">{meetingUrl}</span>
                        <button
                          onClick={handleCopyLink}
                          className="px-2 py-1 rounded-lg bg-purple-600 hover:bg-purple-700 text-[11px] font-semibold text-white flex items-center gap-1 cursor-pointer"
                        >
                          {copiedLink ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                          <span>{copiedLink ? 'Copied' : 'Copy Link'}</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* QR Code */}
                  <div className="flex flex-col items-center p-4 rounded-2xl bg-black/40 border border-white/10 text-center">
                    <canvas ref={qrCanvasRef} className="rounded-xl shadow-lg mb-2" />
                    <span className="text-xs font-bold text-white/80">Scan with Phone Camera</span>
                    <span className="text-[10px] text-white/40">Instantly joins group meeting</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Floating Bottom Controls Dock */}
      <div className="h-20 bg-black/70 backdrop-blur-2xl border-t border-white/10 px-4 md:px-8 flex items-center justify-between shrink-0 z-30">
        {/* Left: Meeting Name / Status */}
        <div className="hidden lg:flex items-center gap-3">
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
          <div>
            <span className="text-xs font-bold text-white block">MeetSplit Group</span>
            <span className="text-[10px] font-mono text-white/40">{activeRoomCode}</span>
          </div>
        </div>

        {/* Center: Core Control Buttons */}
        <div className="flex items-center gap-2 md:gap-3 mx-auto">
          {/* Microphone */}
          <button
            onClick={toggleMic}
            className={`p-3 md:p-3.5 rounded-2xl backdrop-blur-xl transition-all cursor-pointer shadow-lg ${
              isMicMuted
                ? 'bg-red-500 hover:bg-red-600 text-white ring-2 ring-red-500/50'
                : 'bg-white/10 hover:bg-white/20 text-white border border-white/10'
            }`}
            title={isMicMuted ? 'Unmute Mic' : 'Mute Mic'}
          >
            {isMicMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          </button>

          {/* Camera */}
          <button
            onClick={toggleVideo}
            className={`p-3 md:p-3.5 rounded-2xl backdrop-blur-xl transition-all cursor-pointer shadow-lg ${
              isVideoMuted
                ? 'bg-red-500 hover:bg-red-600 text-white ring-2 ring-red-500/50'
                : 'bg-white/10 hover:bg-white/20 text-white border border-white/10'
            }`}
            title={isVideoMuted ? 'Turn Camera On' : 'Turn Camera Off'}
          >
            {isVideoMuted ? <VideoOff className="w-5 h-5" /> : <Video className="w-5 h-5" />}
          </button>

          {/* Flip Camera on Mobile during meeting */}
          {isMobileDevice && (
            <button
              onClick={handleFlipMobileCamera}
              disabled={isFlippingCamera}
              className="p-3 md:p-3.5 rounded-2xl backdrop-blur-xl transition-all cursor-pointer shadow-lg bg-white/10 hover:bg-white/20 text-white border border-white/10 active:scale-95"
              title="Flip Camera (Front/Back)"
            >
              <SwitchCamera className={`w-5 h-5 text-indigo-400 ${isFlippingCamera ? 'animate-spin' : ''}`} />
            </button>
          )}

          {/* Screen Share */}
          <button
            onClick={toggleScreenShare}
            className={`p-3 md:p-3.5 rounded-2xl backdrop-blur-xl transition-all cursor-pointer shadow-lg ${
              isScreenSharing
                ? 'bg-blue-600 hover:bg-blue-700 text-white ring-2 ring-blue-500/50'
                : 'bg-white/10 hover:bg-white/20 text-white border border-white/10'
            }`}
            title={isScreenSharing ? 'Stop Screen Sharing' : 'Share Screen'}
          >
            <Share2 className="w-5 h-5" />
          </button>

          {/* Raise Hand */}
          <button
            onClick={handleRaiseHand}
            className={`p-3 md:p-3.5 rounded-2xl backdrop-blur-xl transition-all cursor-pointer shadow-lg ${
              isHandRaised
                ? 'bg-amber-500 hover:bg-amber-600 text-white ring-2 ring-amber-500/50'
                : 'bg-white/10 hover:bg-white/20 text-white border border-white/10'
            }`}
            title="Raise Hand"
          >
            <Hand className="w-5 h-5" />
          </button>

          {/* Record Meeting */}
          <button
            onClick={handleToggleRecording}
            className={`p-3 md:p-3.5 rounded-2xl backdrop-blur-xl transition-all cursor-pointer shadow-lg ${
              isRecording
                ? 'bg-red-600 hover:bg-red-700 text-white animate-pulse ring-2 ring-red-500/50'
                : 'bg-white/10 hover:bg-white/20 text-white border border-white/10'
            }`}
            title={isRecording ? 'Stop Recording' : 'Record Group Meeting'}
          >
            <Disc className="w-5 h-5" />
          </button>

          {/* Quick Emoji Reactions */}
          <div className="hidden sm:flex items-center gap-1 px-2 py-1.5 rounded-2xl bg-white/5 border border-white/10">
            {['👏', '❤️', '👍', '🎉'].map(emoji => (
              <button
                key={emoji}
                onClick={() => handleSendReaction(emoji)}
                className="px-1.5 py-1 text-sm hover:scale-125 transition-transform"
                title={`React ${emoji}`}
              >
                {emoji}
              </button>
            ))}
          </div>

          {/* End / Leave Call Button */}
          <button
            onClick={handleLeaveMeeting}
            className="px-5 py-3 md:py-3.5 rounded-2xl bg-red-600 hover:bg-red-700 active:scale-95 text-white font-bold text-xs md:text-sm shadow-xl shadow-red-600/40 transition-all flex items-center gap-2 cursor-pointer ml-2"
            title="Leave Meeting"
          >
            <PhoneOff className="w-5 h-5" />
            <span className="hidden md:inline">Leave</span>
          </button>
        </div>

        {/* Right: Drawer Toggles */}
        <div className="flex items-center gap-2">
          {/* Chat Toggle */}
          <button
            onClick={() => {
              setActiveDrawer(curr => (curr === 'chat' ? 'none' : 'chat'));
              setUnreadChatCount(0);
            }}
            className={`p-3 rounded-2xl transition-all cursor-pointer relative ${
              activeDrawer === 'chat'
                ? 'bg-purple-600 text-white'
                : 'bg-white/10 hover:bg-white/20 text-white/80'
            }`}
            title="Open In-Meeting Chat"
          >
            <MessageSquare className="w-5 h-5" />
            {unreadChatCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-[#0a0f1d]">
                {unreadChatCount}
              </span>
            )}
          </button>

          {/* People Toggle */}
          <button
            onClick={() => setActiveDrawer(curr => (curr === 'people' ? 'none' : 'people'))}
            className={`p-3 rounded-2xl transition-all cursor-pointer relative ${
              activeDrawer === 'people'
                ? 'bg-purple-600 text-white'
                : 'bg-white/10 hover:bg-white/20 text-white/80'
            }`}
            title="Open Participants Panel"
          >
            <Users className="w-5 h-5" />
            <span className="absolute -top-1 -right-1 px-1.5 py-0.5 rounded-full bg-purple-600 text-white text-[9px] font-bold border-2 border-[#0a0f1d]">
              {participants.length}
            </span>
          </button>

          {/* Device Settings Toggle */}
          <button
            onClick={() => setShowDeviceSettingsModal(true)}
            className={`p-3 rounded-2xl transition-all cursor-pointer ${
              showDeviceSettingsModal
                ? 'bg-purple-600 text-white'
                : 'bg-white/10 hover:bg-white/20 text-white/80'
            }`}
            title="Camera & Microphone Devices"
          >
            <Settings className="w-5 h-5" />
          </button>

          {/* Full Screen Toggle (Room Only) */}
          <button
            onClick={toggleFullscreen}
            className={`p-3 rounded-2xl transition-all cursor-pointer ${
              isFullscreen
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                : 'bg-white/10 hover:bg-white/20 text-white/80'
            }`}
            title={isFullscreen ? 'Exit Full Screen' : 'Full Screen (Room Only)'}
          >
            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
          </button>

          {/* Info Toggle */}
          <button
            onClick={() => setActiveDrawer(curr => (curr === 'info' ? 'none' : 'info'))}
            className={`p-3 rounded-2xl transition-all cursor-pointer ${
              activeDrawer === 'info'
                ? 'bg-purple-600 text-white'
                : 'bg-white/10 hover:bg-white/20 text-white/80'
            }`}
            title="Meeting Details & QR Code"
          >
            <Info className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* In-Meeting Device Settings Modal */}
      {showDeviceSettingsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in">
          <div className="max-w-md w-full rounded-3xl bg-[#0f172a] border border-white/15 p-6 shadow-2xl space-y-5 text-white">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-purple-600/30 border border-purple-500/40 flex items-center justify-center">
                  <Sliders className="w-5 h-5 text-purple-300" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">Audio & Video Devices</h3>
                  <p className="text-xs text-white/50">Auto-detected hardware & live switching</p>
                </div>
              </div>
              <button
                onClick={() => setShowDeviceSettingsModal(false)}
                className="p-1.5 rounded-xl hover:bg-white/10 text-white/60 hover:text-white transition-colors cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {/* Camera Selection */}
              <div>
                <label className="block text-xs font-bold text-white/70 mb-1.5 flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <Video className="w-4 h-4 text-indigo-400" />
                    <span>Select Camera</span>
                  </span>
                  <span className="text-[10px] text-white/40">{videoDevices.length} available</span>
                </label>
                <select
                  value={selectedCameraId}
                  onChange={(e) => handleSelectCamera(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-black/50 border border-white/15 text-white text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
                >
                  {videoDevices.length === 0 ? (
                    <option value="">Default Camera</option>
                  ) : (
                    videoDevices.map((d) => (
                      <option key={d.deviceId} value={d.deviceId} className="bg-slate-900 text-white">
                        {d.label}
                      </option>
                    ))
                  )}
                </select>
              </div>

              {/* Microphone Selection */}
              <div>
                <label className="block text-xs font-bold text-white/70 mb-1.5 flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <Mic className="w-4 h-4 text-purple-400" />
                    <span>Select Microphone</span>
                  </span>
                  <span className="text-[10px] text-white/40">{audioDevices.length} available</span>
                </label>
                <select
                  value={selectedMicId}
                  onChange={(e) => handleSelectMic(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-black/50 border border-white/15 text-white text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
                >
                  {audioDevices.length === 0 ? (
                    <option value="">Default Microphone</option>
                  ) : (
                    audioDevices.map((d) => (
                      <option key={d.deviceId} value={d.deviceId} className="bg-slate-900 text-white">
                        {d.label}
                      </option>
                    ))
                  )}
                </select>

                {/* Mic live level meter */}
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-2 bg-black/50 rounded-full overflow-hidden border border-white/10">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-500 via-teal-400 to-indigo-500 transition-all duration-75"
                      style={{ width: `${isMicMuted ? 0 : micVolumeLevel}%` }}
                    />
                  </div>
                  <span className="text-[10px] font-mono text-white/60 shrink-0">
                    {isMicMuted ? 'Muted' : `${micVolumeLevel}%`}
                  </span>
                </div>
              </div>

              {/* Room Fullscreen Toggle inside modal */}
              <div className="pt-2 border-t border-white/10 flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-white">Room Full Screen</p>
                  <p className="text-[10px] text-white/50">Expand video stage to 100% monitor</p>
                </div>
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  className="px-3 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-md"
                >
                  {isFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
                  <span>{isFullscreen ? 'Exit Full Screen' : 'Full Screen'}</span>
                </button>
              </div>
            </div>

            <button
              onClick={() => setShowDeviceSettingsModal(false)}
              className="w-full py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold text-xs transition-all cursor-pointer"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Share / Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in">
          <div className="max-w-md w-full rounded-3xl bg-[#0f172a] border border-white/15 p-6 md:p-8 shadow-2xl space-y-5 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-purple-600/30 border border-purple-500/40 flex items-center justify-center">
                  <Share2 className="w-5 h-5 text-purple-300" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">Invite Others to Call</h3>
                  <p className="text-xs text-white/50">Anyone with the code or link can join</p>
                </div>
              </div>
              <button
                onClick={() => setShowInviteModal(false)}
                className="p-1 rounded-lg text-white/40 hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {/* Meeting Code */}
              <div>
                <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">
                  Meeting Code
                </label>
                <div className="flex items-center gap-2 p-3 rounded-2xl bg-black/40 border border-white/10">
                  <span className="text-base font-mono font-bold text-purple-300 tracking-wider flex-1">
                    {activeRoomCode}
                  </span>
                  <button
                    onClick={handleCopyCode}
                    className="px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-bold text-white flex items-center gap-1.5"
                  >
                    {copiedCode ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    <span>{copiedCode ? 'Copied' : 'Copy'}</span>
                  </button>
                </div>
              </div>

              {/* Shareable Link */}
              <div>
                <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">
                  Direct Web Link
                </label>
                <div className="flex items-center gap-2 p-3 rounded-2xl bg-black/40 border border-white/10">
                  <span className="text-xs text-white/70 truncate flex-1 font-mono">
                    {meetingUrl}
                  </span>
                  <button
                    onClick={handleCopyLink}
                    className="px-3 py-1.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-xs font-bold text-white flex items-center gap-1.5"
                  >
                    {copiedLink ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    <span>{copiedLink ? 'Copied' : 'Copy Link'}</span>
                  </button>
                </div>
              </div>

              {/* QR Code */}
              <div className="flex flex-col items-center pt-2">
                <canvas ref={qrCanvasRef} className="rounded-xl shadow-lg mb-2" />
                <span className="text-xs text-white/50">Point mobile camera to join immediately</span>
              </div>
            </div>

            <button
              onClick={() => setShowInviteModal(false)}
              className="w-full py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-bold transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// SUBCOMPONENT: PARTICIPANT VIDEO TILE
// ==========================================
interface ParticipantVideoTileProps {
  participant: GroupParticipant;
  isSpeaking?: boolean;
  isPinned?: boolean;
  isThumbnail?: boolean;
  onTogglePin?: () => void;
}

const ParticipantVideoTile: React.FC<ParticipantVideoTileProps> = ({
  participant,
  isSpeaking = false,
  isPinned = false,
  isThumbnail = false,
  onTogglePin
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl || !participant.stream) return;

    if (videoEl.srcObject !== participant.stream) {
      videoEl.srcObject = participant.stream || null;
    }
    videoEl.play().catch(() => {});

    // Listen to track changes (e.g. screen share replaces camera track)
    const handleTrackChange = () => {
      if (videoEl.srcObject !== participant.stream) {
        videoEl.srcObject = participant.stream || null;
      }
      videoEl.play().catch(() => {});
    };

    participant.stream.addEventListener('addtrack', handleTrackChange);
    participant.stream.addEventListener('removetrack', handleTrackChange);

    const videoTrack = participant.stream.getVideoTracks()[0];
    if (videoTrack) {
      videoTrack.addEventListener('unmute', handleTrackChange);
    }

    return () => {
      participant.stream?.removeEventListener('addtrack', handleTrackChange);
      participant.stream?.removeEventListener('removetrack', handleTrackChange);
      if (videoTrack) {
        videoTrack.removeEventListener('unmute', handleTrackChange);
      }
    };
  }, [participant.stream, participant.videoOff, participant.isScreenSharing]);

  const initials = participant.name
    ? participant.name
        .split(' ')
        .map(w => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : 'U';

  const isSharing = Boolean(participant.isScreenSharing);
  // Mirror only local camera, NEVER mirror screen share!
  const isMirrored = participant.isLocal && !isSharing;
  const objectFitStyle = isSharing ? 'object-contain bg-black' : 'object-cover';

  return (
    <div
      className={`relative w-full h-full flex items-center justify-center overflow-hidden transition-all duration-200 ${
        isSpeaking ? 'ring-4 ring-emerald-500 shadow-[0_0_30px_rgba(16,185,129,0.35)]' : ''
      }`}
    >
      {/* Video Element */}
      {participant.stream && (!participant.videoOff || isSharing) ? (
        <video
          ref={videoRef}
          data-participant-id={participant.id}
          data-is-sharing={isSharing ? 'true' : 'false'}
          autoPlay
          playsInline
          muted={participant.isLocal} // Local video is muted to prevent local audio feedback
          className={`w-full h-full ${objectFitStyle} ${isMirrored ? 'scale-x-[-1]' : ''}`}
        />
      ) : (
        /* Video Off Avatar */
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="relative">
            {isSpeaking && (
              <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-40 scale-125" />
            )}
            <div
              className={`w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-tr ${
                participant.avatarBg || 'from-purple-600 to-indigo-600'
              } flex items-center justify-center text-2xl md:text-3xl font-black text-white shadow-2xl border-2 border-white/20`}
            >
              {initials}
            </div>
          </div>
          <span className="text-xs font-semibold text-white/60">{participant.name}</span>
        </div>
      )}

      {/* Screen Sharing Presentation Badge */}
      {isSharing && (
        <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-600/90 backdrop-blur-md text-white text-xs font-bold shadow-lg">
          <Share2 className="w-3.5 h-3.5 text-cyan-200 animate-pulse" />
          <span>{participant.isLocal ? 'You are presenting' : `${participant.name} is presenting`}</span>
        </div>
      )}

      {/* Floating Hand Raised Badge */}
      {participant.handRaised && (
        <div className="absolute top-3 left-3 flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/90 backdrop-blur-md text-white text-xs font-bold shadow-xl animate-bounce">
          <Hand className="w-3.5 h-3.5" />
          <span>Hand Raised</span>
        </div>
      )}

      {/* Pin button on hover */}
      {!isThumbnail && onTogglePin && (
        <button
          onClick={onTogglePin}
          className="absolute top-3 right-3 p-2 rounded-xl bg-black/60 hover:bg-black/80 backdrop-blur-md text-white/70 hover:text-white border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
          title={isPinned ? 'Unpin' : 'Pin to screen'}
        >
          {isPinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
        </button>
      )}

      {/* Bottom Name Banner */}
      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between pointer-events-none">
        <div className="flex items-center gap-2 px-3 py-1 rounded-xl bg-black/60 backdrop-blur-md border border-white/10 text-xs font-semibold text-white max-w-[85%] truncate">
          <span className="truncate">{participant.name}</span>
          {participant.isLocal && <span className="text-[10px] text-white/50">(You)</span>}
          {participant.isHost && (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-purple-500/30 text-purple-300 border border-purple-500/50">
              Host
            </span>
          )}
        </div>

        {/* Audio Mute Icon */}
        <div className="p-1.5 rounded-xl bg-black/60 backdrop-blur-md border border-white/10 shrink-0">
          {participant.audioMuted ? (
            <MicOff className="w-3.5 h-3.5 text-red-400" />
          ) : (
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <span className={`w-2 h-2 rounded-full ${isSpeaking ? 'bg-emerald-400 animate-ping' : 'bg-emerald-400'}`} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
